import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { ArrowRight, Play, Clock } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { isJobMine } from '../lib/utils';

interface DayJob {
  id: string;
  job_number?: string;
  title: string;
  time?: string;
  status: string;
  assigned_to: string;
  client_name?: string;
  date: string;
}

// Priority for picking the "next" job
const STATUS_PRIORITY: Record<string, number> = {
  'In Progress': 0,
  Pending: 1,
  'Follow-up Visit Required': 2,
  'Awaiting Parts': 3,
};

const HIDE_PATHS = ['/login'];

export default function NextJobFloat() {
  const { user, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<DayJob[]>([]);
  const [refreshTick, setRefreshTick] = useState(0);

  // Fetch today's jobs (only for workers)
  useEffect(() => {
    if (isAdmin || !user) return;
    const today = format(new Date(), 'yyyy-MM-dd');
    (async () => {
      try {
        const res = await api.get(`/jobs/date/${today}`);
        setJobs(res.data || []);
      } catch (e) {
        setJobs([]);
      }
    })();
  }, [isAdmin, user, refreshTick, location.pathname]);

  // Recompute every 60s to catch time-based progression
  useEffect(() => {
    if (isAdmin || !user) return;
    const t = setInterval(() => setRefreshTick((x) => x + 1), 60000);
    return () => clearInterval(t);
  }, [isAdmin, user]);

  // Filter to mine and open + sort
  const mine = useMemo(() => {
    return jobs
      .filter((j) => isJobMine(j as any, user))
      .filter((j) => !['Completed', 'Invoiced', 'Cancelled'].includes(j.status))
      .sort((a, b) => {
        const p =
          (STATUS_PRIORITY[a.status] ?? 99) - (STATUS_PRIORITY[b.status] ?? 99);
        if (p !== 0) return p;
        return (a.time || '99:99').localeCompare(b.time || '99:99');
      });
  }, [jobs, user]);

  // Determine current job id from URL if we're on a job page
  const currentJobId = useMemo(() => {
    const m = location.pathname.match(/^\/jobs\/([^/]+)$/);
    return m ? m[1] : null;
  }, [location.pathname]);

  // "Next job" = the first one that is NOT the currently-open one
  const nextJob = useMemo(() => {
    return mine.find((j) => j.id !== currentJobId) || null;
  }, [mine, currentJobId]);

  const shouldHide =
    isAdmin ||
    !user ||
    !nextJob ||
    HIDE_PATHS.some((p) => location.pathname === p);

  if (shouldHide) return null;

  // Two rendering styles:
  // - On a job page: compact FAB (bottom-right) so it doesn't crowd
  // - Elsewhere: full-width toast-style bar at bottom
  const onJobPage = !!currentJobId;

  return onJobPage ? (
    <button
      onClick={() => navigate(`/jobs/${nextJob.id}`)}
      className="fixed z-40 bottom-5 right-5 md:bottom-6 md:right-6 shadow-pop bg-brand-600 hover:bg-brand-700 text-white rounded-full pl-5 pr-4 py-3 flex items-center gap-3 text-sm font-semibold transition group max-w-[85vw]"
      aria-label="Go to next job"
    >
      <div className="text-left min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-white/70 font-bold leading-none">
          Next up
        </div>
        <div className="truncate max-w-[200px] mt-0.5 leading-tight">
          {nextJob.title}
        </div>
      </div>
      <ArrowRight className="h-4 w-4 shrink-0 group-hover:translate-x-0.5 transition" />
    </button>
  ) : (
    <div className="fixed z-40 bottom-4 inset-x-4 md:bottom-6 md:right-6 md:left-auto md:w-[360px]">
      <button
        onClick={() => navigate(`/jobs/${nextJob.id}`)}
        className="w-full shadow-pop bg-white border border-brand-200 hover:border-brand-400 rounded-2xl p-3 flex items-center gap-3 text-left transition group"
      >
        <div className="w-10 h-10 rounded-xl bg-brand-600 text-white grid place-items-center shrink-0">
          {nextJob.status === 'In Progress' ? (
            <Play className="h-4 w-4" />
          ) : (
            <Clock className="h-4 w-4" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-brand-700 font-bold">
            {nextJob.status === 'In Progress' ? 'Continue current job' : 'Next up'}
            {nextJob.time && ` · ${nextJob.time}`}
          </div>
          <div className="font-semibold text-slate-900 truncate leading-tight mt-0.5">
            {nextJob.title}
          </div>
          {nextJob.client_name && (
            <div className="text-xs text-slate-500 truncate">
              {nextJob.client_name}
            </div>
          )}
        </div>
        <ArrowRight className="h-4 w-4 shrink-0 text-brand-600 group-hover:translate-x-0.5 transition" />
      </button>
    </div>
  );
}
